import {
  isCustomerFacingLocationSegment,
  meaningfulLegacyPlacement,
  resolveSafetyLocationDisplay,
  type CameraPlacementLookup,
  type SafetyLocationLookups,
} from './locationDisplay';
import {
  resolveConfiguredCameraLabel,
  resolveConfiguredZoneLabel,
} from '../domain/customerLabels/customerFacingControlLabel';

export type CustomerPlaceSource = {
  camera_id?: string | null;
  zone_id?: string | null;
  zone_label?: string | null;
  area_label?: string | null;
  site_label?: string | null;
  site_id?: string | null;
  camera_label?: string | null;
};

const PEER_SITE_BUYER_ALIAS_RE = /warehouse\s*(?:room)?\s*#?\s*\d+/i;

/** True when a buyer nickname looks like a peer warehouse/site, not an area. */
export function isPeerSiteBuyerAlias(value?: string | null): boolean {
  const text = String(value || '').trim();
  if (!text) return false;
  return new RegExp(`^${PEER_SITE_BUYER_ALIAS_RE.source}`, 'i').test(text);
}

/** True when any segment of a title/label embeds a peer-site buyer nickname. */
export function textContainsPeerSiteBuyerAlias(value?: string | null): boolean {
  return PEER_SITE_BUYER_ALIAS_RE.test(String(value || ''));
}

/**
 * Drop peer-site nickname segments (Warehouse #2) from a synthesized title.
 * Keeps pattern text such as "Near Miss".
 */
export function stripPeerSiteBuyerAliasSegments(value?: string | null): string {
  const text = String(value || '').trim();
  if (!text) return '';
  return text
    .split(/\s+-\s+/)
    .map((part) => part.trim())
    .filter((part) => {
      if (!part) return false;
      if (isPeerSiteBuyerAlias(part)) return false;
      // "Zone Warehouse #2" / "Camera Warehouse #2"
      if (/^(zone|camera|device)\s+/i.test(part) && textContainsPeerSiteBuyerAlias(part)) {
        return false;
      }
      return true;
    })
    .join(' - ')
    .replace(/\s*[·|]\s*Warehouse\s*(?:Room)?\s*#?\s*\d+/gi, '')
    .trim();
}

/**
 * Intentional area aliases that are real places (not peer-site nicknames).
 * Peer-site maps like worksafety→Warehouse #2 are intentionally omitted.
 */
export function resolveIntentionalAreaAlias(value?: string | null): string | null {
  const text = String(value || '').trim();
  if (!text) return null;
  const configured = resolveConfiguredZoneLabel(text);
  if (configured) return configured;
  const compact = text.toLowerCase().replace(/[_\-\s]+/g, '');
  if (compact === 'demo5tes' || compact === 'demotestarea') {
    return 'Restricted Forklift Zone';
  }
  return null;
}

function isSelectedSiteIdentity(
  value: string,
  lookups: SafetyLocationLookups,
  row: CustomerPlaceSource,
): boolean {
  const lower = value.trim().toLowerCase();
  if (!lower) return false;
  const siteNames = [
    lookups.siteDisplayName,
    lookups.siteId,
    row.site_label,
    row.site_id,
  ]
    .map((part) => String(part || '').trim().toLowerCase())
    .filter(Boolean);
  return siteNames.includes(lower);
}

/**
 * Location API place under the selected site: area_name [/ mount_label].
 * Uses the same join the placements API uses for `path` (Site / Area / Mount),
 * minus site (header already shows it). Mount comes from the API field as-is —
 * do not strip registry mounts via customer-chrome forbid heuristics.
 */
function placeLabelFromApiPlacement(placement: CameraPlacementLookup): string {
  const area = meaningfulLegacyPlacement(placement.areaName)
    || (
      isCustomerFacingLocationSegment(placement.areaId)
        ? meaningfulLegacyPlacement(placement.areaId)
        : ''
    );
  const mount = meaningfulLegacyPlacement(placement.mountLabel);
  return [area, mount].filter(Boolean).join(' / ');
}

/**
 * Area (+ optional mount) segments under the selected site from a physical path.
 * Drops the leading site display name — header/filter already shows site context.
 */
function placeLabelFromPhysicalPath(
  primary: string,
  lookups: SafetyLocationLookups,
  row: CustomerPlaceSource,
): string {
  const parts = primary
    .split(/\s*\/\s*/)
    .map((part) => part.trim())
    .filter((part) => isCustomerFacingLocationSegment(part))
    .filter((part) => !isSelectedSiteIdentity(part, lookups, row))
    // Drop camera nicknames that read as peer warehouses (Warehouse #2).
    .filter((part) => !isPeerSiteBuyerAlias(part));
  return parts.join(' / ');
}

/**
 * Customer place label under the selected site (Overview, Cases, Live Cameras).
 *
 * SSOT: GET …/camera-placements fields `area_name` + `mount_label` (API path
 * composition with ` / `, site omitted). Fallbacks: customer-safe area/zone
 * label → intentional area alias → omit (`—`).
 * Never invent separators, buyer-alias maps, or Demo* chrome from raw ids.
 */
export function resolveCustomerPlaceLabel(
  row: CustomerPlaceSource,
  lookups: SafetyLocationLookups = {},
  opts?: {
    zoneLabel?: string | null;
    cameraName?: string | null;
  },
): string {
  const cameraId = String(row.camera_id || '').trim();
  const placement = cameraId
    ? lookups.cameraPlacementById?.[cameraId]
    : undefined;
  if (placement) {
    const fromApi = placeLabelFromApiPlacement(placement);
    if (fromApi) return fromApi;
  }

  const display = resolveSafetyLocationDisplay(
    {
      camera_id: row.camera_id,
      zone_id: row.zone_id,
      zoneLabel: opts?.zoneLabel || row.zone_label || row.area_label,
      areaLabel: row.area_label,
      siteLabel: row.site_label || row.site_id,
      cameraName: opts?.cameraName || row.camera_label,
    },
    lookups,
  );

  if (display.primary && display.primary !== 'Location not recorded') {
    const underSite = placeLabelFromPhysicalPath(display.primary, lookups, row);
    if (underSite) return underSite;
  }

  for (const candidate of [opts?.zoneLabel, row.zone_label, row.area_label]) {
    const text = String(candidate || '').trim();
    if (!text || !isCustomerFacingLocationSegment(text)) continue;
    if (isSelectedSiteIdentity(text, lookups, row)) continue;
    if (isPeerSiteBuyerAlias(text)) continue;
    return text;
  }

  const aliasCandidates = [
    row.zone_id,
    opts?.zoneLabel,
    row.zone_label,
    row.area_label,
  ];
  for (const candidate of aliasCandidates) {
    const alias = resolveIntentionalAreaAlias(candidate);
    if (alias) return alias;
  }

  return '—';
}

/**
 * Live Cameras card title: prefer registry area when catalog name is a
 * peer-site buyer nickname (Warehouse #2).
 */
export function resolveLiveCameraDisplayTitle(
  cameraName: string | null | undefined,
  cameraId: string | null | undefined,
  placement?: { areaName?: string | null } | null,
): string {
  const areaName = String(placement?.areaName || '').trim();
  const rawName = String(cameraName || '').trim();
  if (isPeerSiteBuyerAlias(rawName) && areaName) return areaName;
  return rawName || areaName || String(cameraId || '').trim() || 'Camera';
}

/**
 * Camera / scope label for measurement health and open-work chrome.
 * Prefer registry place; never "Camera Warehouse #2".
 */
export function resolveCustomerCameraScopeLabel(
  cameraId: string | null | undefined,
  lookups: SafetyLocationLookups = {},
  opts?: {
    zoneId?: string | null;
    zoneLabel?: string | null;
    cameraName?: string | null;
  },
): string {
  const id = String(cameraId || '').trim();
  if (!id) return '—';
  const place = resolveCustomerPlaceLabel(
    {
      camera_id: id,
      zone_id: opts?.zoneId,
      zone_label: opts?.zoneLabel,
      camera_label: opts?.cameraName,
    },
    lookups,
    { zoneLabel: opts?.zoneLabel, cameraName: opts?.cameraName },
  );
  if (place && place !== '—') return place;
  const catalog = String(opts?.cameraName || '').trim();
  if (
    catalog
    && isCustomerFacingLocationSegment(catalog)
    && !isPeerSiteBuyerAlias(catalog)
  ) {
    return catalog;
  }
  return '—';
}

function looksLikeRawId(value: string, rawId?: string | null): boolean {
  const text = String(value || '').trim();
  if (!text) return true;
  const id = String(rawId || '').trim();
  if (id && text.toLowerCase() === id.toLowerCase()) return true;
  if (/^cam[_-]/i.test(text)) return true;
  if (/^[a-z0-9]+(?:_[a-z0-9]+)+$/.test(text) && !/\s/.test(text)) return true;
  return false;
}

/** Pattern Evidence Review zone label. Never show raw zone IDs such as worksafety. */
export function resolvePatternEvidenceZoneLabel(args: {
  zoneId?: string | null;
  zoneName?: string | null;
}): string {
  const fromId = resolveConfiguredZoneLabel(args.zoneId) || resolveIntentionalAreaAlias(args.zoneId);
  if (fromId) return fromId;
  for (const candidate of [args.zoneName, args.zoneId]) {
    const alias = resolveIntentionalAreaAlias(candidate) || resolveConfiguredZoneLabel(candidate);
    if (alias) return alias;
  }
  const named = String(args.zoneName || '').trim();
  if (named && isCustomerFacingLocationSegment(named) && !looksLikeRawId(named, args.zoneId)) {
    return named;
  }
  return 'Monitored zone';
}

/** Pattern Evidence Review camera/place label. Never show raw camera IDs such as cam_walkway_zone_demo. */
export function resolvePatternEvidenceCameraLabel(args: {
  cameraId?: string | null;
  cameraName?: string | null;
  zoneId?: string | null;
  zoneName?: string | null;
  locationLine?: string | null;
  lookups?: SafetyLocationLookups;
}): string {
  const configured = resolveConfiguredCameraLabel(args.cameraId) || resolveConfiguredCameraLabel(args.cameraName);
  if (configured) return configured;
  const place = resolveCustomerPlaceLabel(
    {
      camera_id: args.cameraId,
      zone_id: args.zoneId,
      zone_label: args.zoneName,
      camera_label: args.cameraName,
    },
    args.lookups || {},
    { zoneLabel: args.zoneName, cameraName: args.cameraName },
  );
  if (place && place !== '—' && !looksLikeRawId(place, args.cameraId) && !looksLikeRawId(place, args.zoneId)) {
    return place;
  }
  const location = String(args.locationLine || '').trim();
  if (location && isCustomerFacingLocationSegment(location)) {
    const cleaned = location
      .split(/\s*[·|,]\s*/)
      .map((part) => part.trim())
      .filter((part) => part && !looksLikeRawId(part, args.cameraId) && !looksLikeRawId(part, args.zoneId))
      .join(' - ');
    if (cleaned && !looksLikeRawId(cleaned, args.cameraId)) return cleaned;
  }
  const catalog = String(args.cameraName || '').trim();
  if (catalog && isCustomerFacingLocationSegment(catalog) && !looksLikeRawId(catalog, args.cameraId)) {
    return catalog;
  }
  return 'Camera name unavailable';
}
